import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview MapSheetContent — shared presentational layer for the map
 * bottom sheet (mobile) and right rail (desktop). Renders the sticky eyebrow
 * header with `IN VIEW · N EVENTS` count, optional expand/collapse toggle,
 * scrollable list of `MapSheetRow`s, and loading + empty states. Auto-scrolls
 * the active row into view when `activeEventId` changes, respecting
 * `prefers-reduced-motion` and the optional `skipAutoScrollRef` guard.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useTheme } from '../../../theme';
import { MapSheetRow } from '../MapSheetRow';
import { MapSheetToggle } from './MapSheetToggle';
const SKELETON_COUNT = 6;
function countLabel(count) {
    const noun = count === 1 ? 'EVENT' : 'EVENTS';
    return `IN VIEW · ${count} ${noun}`;
}
function RowSkeleton({ theme }) {
    const wrapperStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    };
    const coverStyle = {
        width: 64,
        height: 64,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.surfaceAlt,
        opacity: 0.6,
    };
    const barStyle = (width) => ({
        height: 10,
        width,
        borderRadius: 4,
        backgroundColor: theme.colors.surfaceAlt,
        opacity: 0.6,
    });
    return (_jsx("li", { "aria-hidden": "true", "data-testid": "map-sheet-skeleton-row", style: { listStyle: 'none' }, children: _jsxs("div", { style: wrapperStyle, children: [_jsx("div", { style: coverStyle }), _jsxs("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }, children: [_jsx("div", { style: barStyle('70%') }), _jsx("div", { style: barStyle('45%') })] })] }) }));
}
export const MapSheetContent = forwardRef(({ events, activeEventId, onEventSelect, isLoading = false, isExpanded, onToggleExpanded, skipAutoScrollRef, className, 'data-testid': dataTestId = 'map-sheet-content', }, ref) => {
    const theme = useTheme();
    const rowRefs = useRef(new Map());
    const registerRef = useCallback((id, el) => {
        if (el) {
            rowRefs.current.set(id, el);
        }
        else {
            rowRefs.current.delete(id);
        }
    }, []);
    const scrollEventIntoView = useCallback((eventId) => {
        const el = rowRefs.current.get(eventId);
        if (!el || typeof el.scrollIntoView !== 'function')
            return;
        const prefersReducedMotion = typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'nearest',
        });
    }, []);
    useImperativeHandle(ref, () => ({ scrollEventIntoView }), [scrollEventIntoView]);
    useEffect(() => {
        if (!activeEventId || skipAutoScrollRef?.current)
            return;
        scrollEventIntoView(activeEventId);
    }, [activeEventId, skipAutoScrollRef, scrollEventIntoView]);
    const count = events.length;
    const rootStyle = {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: theme.colors.surface,
        color: theme.colors.text,
    };
    const headerStyle = {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.md,
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        background: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.border}`,
    };
    const eyebrowStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '11px',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const listStyle = {
        listStyle: 'none',
        margin: 0,
        padding: `${theme.spacing.xs}px ${theme.spacing.xs}px ${theme.spacing.md}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    };
    const emptyStyle = {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing.xl,
        textAlign: 'center',
        color: theme.colors.textMuted,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
    };
    const showToggle = typeof isExpanded === 'boolean' && onToggleExpanded !== undefined;
    return (_jsxs("div", { className: className, "data-testid": dataTestId, style: rootStyle, "aria-label": "Events in view", children: [_jsxs("div", { style: headerStyle, children: [_jsx("span", { style: eyebrowStyle, "data-testid": "map-sheet-eyebrow", children: isLoading ? 'IN VIEW · LOADING' : countLabel(count) }), showToggle ? (_jsx(MapSheetToggle, { isExpanded: isExpanded, onToggle: onToggleExpanded })) : null] }), isLoading ? (_jsx("ul", { role: "status", "aria-busy": "true", "aria-label": "Loading events", style: { ...listStyle, overflow: 'hidden' }, children: Array.from({ length: SKELETON_COUNT }, (_, i) => (_jsx(RowSkeleton, { theme: theme }, i))) })) : count === 0 ? (_jsx("div", { style: emptyStyle, "data-testid": "map-sheet-empty", children: "No events in this area. Pan or zoom the map to find more." })) : (_jsx("ul", { role: "list", style: listStyle, "data-testid": "map-sheet-list", children: events.map((event) => (_jsx("li", { style: { listStyle: 'none' }, children: _jsx(MapSheetRow, { event: event, isActive: event.id === activeEventId, onSelect: onEventSelect, ref: (el) => registerRef(event.id, el) }) }, event.id))) }))] }));
});
MapSheetContent.displayName = 'MapSheetContent';
